import React, { ReactNode, useState, useEffect } from 'react';
import { useStepNavigation, useDecisionVoucherContext } from '../../context';
import { DecisionVoucherV2Step } from '../../models/decisionVoucherV2';
import { useInfoDisplay, InfoDisplayType } from '../../context/InfoDisplayContext';
import useCurrentSav from '../../../../../../Hooks/useCurrentSav';

interface MainColumnProps {
  children?: ReactNode;
}

const MainColumn: React.FC<MainColumnProps> = ({ children }) => {
  const { currentStep } = useStepNavigation();
  const context = useDecisionVoucherContext();
  const [isLoading, setIsLoading] = useState(true);
  const { showInfo, isInfoVisible } = useInfoDisplay();
  const { sav } = useCurrentSav();

  // Charger une seule fois ou quand l'ID du voucher change
  useEffect(() => {
    if (context.voucher) {
      setIsLoading(false);
    }
  }, [context.voucher?.id]); // Dépendance sur l'ID uniquement, pas sur tout l'objet voucher

  // Titres et descriptions pour chaque étape
  const stepContent = {
    [DecisionVoucherV2Step.QUALIFICATION]: {
      title: 'Qualification du dossier',
      description: 'Évaluez l\'éligibilité du dossier selon les critères contractuels'
    },
    [DecisionVoucherV2Step.CONFIGURATION]: {
      title: 'Configuration de la décision',
      description: 'Configurez les détails de l\'accord ou du refus'
    },
    [DecisionVoucherV2Step.VALIDATION]: {
      title: 'Validation et envoi',
      description: 'Vérifiez et finalisez votre décision avant envoi'
    }
  };

  // Valeur par défaut si currentStep est undefined
  const currentStepSafe = currentStep || DecisionVoucherV2Step.QUALIFICATION;
  const { title, description } = stepContent[currentStepSafe] || stepContent[DecisionVoucherV2Step.QUALIFICATION];

  return (
    <div className="tw-flex tw-flex-col tw-h-full">
      {/* En-tête de l'étape */}
      <div className="tw-border-b tw-border-gray-200 tw-px-4 tw-py-3">
        <div className="tw-flex tw-justify-between tw-items-center">
          <div>
            <h2 className="tw-text-xl tw-font-semibold tw-text-gray-800">{title}</h2>
            <p className="tw-text-sm tw-text-gray-600 tw-mt-1">{description}</p>
          </div>
          {sav && (
            <div className="tw-flex tw-space-x-2">
              <InfoButton
                icon="fa-file-contract"
                label="Contrat"
                onClick={() => showInfo(InfoDisplayType.CONTRACT)}
                isActive={isInfoVisible(InfoDisplayType.CONTRACT)}
              />
              <InfoButton
                icon="fa-car"
                label="Véhicule"
                onClick={() => showInfo(InfoDisplayType.VEHICLE)}
                isActive={isInfoVisible(InfoDisplayType.VEHICLE)}
              />
              <InfoButton
                icon="fa-user"
                label="Client"
                onClick={() => showInfo(InfoDisplayType.CUSTOMER)}
                isActive={isInfoVisible(InfoDisplayType.CUSTOMER)}
              />
              <InfoButton
                icon="fa-file-invoice-dollar"
                label="Devis"
                onClick={() => showInfo(InfoDisplayType.QUOTE)}
                isActive={isInfoVisible(InfoDisplayType.QUOTE)}
              />
            </div>
          )}
        </div>
      </div>

      {/* Contenu principal */}
      <div className="tw-flex-grow tw-overflow-y-auto tw-p-4">
        {isLoading ? (
          <div className="tw-flex tw-justify-center tw-items-center tw-h-full">
            <div className="tw-animate-spin tw-rounded-full tw-h-10 tw-w-10 tw-border-t-2 tw-border-b-2 tw-border-blue-500"></div>
          </div>
        ) : (
          <div className="tw-space-y-6">
            {children}
          </div>
        )}
      </div>

      {/* Barre de progression au bas */}
      <div className="tw-px-4 tw-py-3 tw-border-t tw-border-gray-200">
        <div className="tw-flex tw-items-center tw-text-sm tw-text-gray-500">
          <span>Étape {currentStepSafe === DecisionVoucherV2Step.QUALIFICATION ? '1' :
                      currentStepSafe === DecisionVoucherV2Step.CONFIGURATION ? '2' : '3'} sur 3</span>
          <div className="tw-flex-grow tw-ml-4 tw-bg-gray-200 tw-rounded-full tw-h-2.5">
            <div
              className="tw-bg-blue-600 tw-h-2.5 tw-rounded-full"
              style={{
                width: currentStepSafe === DecisionVoucherV2Step.QUALIFICATION ? '33%' :
                      currentStepSafe === DecisionVoucherV2Step.CONFIGURATION ? '66%' : '100%'
              }}
            ></div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Composant pour les boutons d'information
interface InfoButtonProps {
  icon: string;
  label: string;
  onClick: () => void;
  isActive: boolean;
}

const InfoButton: React.FC<InfoButtonProps> = ({ icon, label, onClick, isActive }) => {
  return (
    <button
      className={`tw-flex tw-items-center tw-px-3 tw-py-1.5 tw-rounded tw-text-sm tw-focus:outline-none ${
        isActive
          ? 'tw-bg-blue-50 tw-text-blue-700 tw-border tw-border-blue-300 tw-focus:bg-blue-50'
          : 'tw-bg-gray-50 tw-text-gray-600 hover:tw-bg-gray-100 tw-border tw-border-gray-200 tw-focus:bg-gray-50'
      }`}
      onClick={onClick}
      title={isActive ? `Fermer les informations ${label.toLowerCase()}` : `Afficher les informations ${label.toLowerCase()}`}
    >
      <i className={`fas ${icon} tw-mr-1.5`}></i>
      <span>{label}</span>
      {isActive && (
        <i className="fas fa-times tw-ml-1.5 tw-text-blue-500" aria-hidden="true"></i>
      )}
    </button>
  );
};

export default MainColumn;